import { StarIcon } from "@/Icons/v2/DatePickerIcons/Star";
import { useConfigService } from "@/pages/configuration/useConfigService";
import { useTrans } from "@/translations";

import { useCallback, useMemo } from "react";
import { Fragment } from "react/jsx-runtime";

import type { FilterTimeFrameValue } from "server/config-api/index.js";
import { twMerge } from "tailwind-merge";

import { PRESET_GROUPS, Preset, type PresetKey } from "../constants";

type Props = {
  selectedPreset: PresetKey;
  selectPreset: (key: PresetKey) => void;
};

export const PresetButton: React.FC<Props & { preset: Preset }> = ({
  preset,
  selectedPreset,
  selectPreset,
}) => {
  const { t } = useTrans();
  const { terminalConfig, updateTerminalConfig } = useConfigService();

  const isCurrent = useMemo(
    () => selectedPreset === preset.key,
    [selectedPreset, preset],
  );
  const isDefault = useMemo(
    () => terminalConfig?.defaultTimeFrame === preset.key,
    [terminalConfig, preset],
  );

  const markAsDefault = useCallback(
    (key: PresetKey) => {
      updateTerminalConfig({
        terminalId: terminalConfig?.terminalUuid!,
        defaultTimeFrame: key as FilterTimeFrameValue | null,
      });
    },
    [terminalConfig, updateTerminalConfig],
  );

  return (
    <div key={preset.key} className="group relative">
      <button
        type="button"
        onClick={() => selectPreset(preset.key)}
        className={twMerge(
          "font-dm-sans w-full truncate rounded-md border py-2 pr-[30px] pl-[10px] text-left text-[12px]",
          isCurrent
            ? "bg-lime-light border-lime font-semibold"
            : "border-grey2 hover:bg-grey1",
        )}
      >
        {t(preset.label)}
      </button>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          markAsDefault(preset.key);
        }}
        className={twMerge(
          "hover:bg-grey2 absolute top-1/2 right-[5px] flex h-[22px] w-[22px] -translate-y-1/2 items-center justify-center rounded",
          isDefault ? "opacity-100" : "opacity-0 group-hover:opacity-100",
        )}
      >
        <StarIcon filled={isDefault} />
      </button>
    </div>
  );
};

export const PresetsView: React.FC<Props> = (props) => {
  const { t } = useTrans();

  return (
    <>
      <div className="font-dm-sans text-grey6 px-[14px] pt-[12px] text-[10px] font-bold tracking-[0.06] tracking-wider uppercase">
        {t("datePicker:presetsTitle")}
      </div>
      <div>
        {PRESET_GROUPS.map((group, gi) => (
          <Fragment key={gi}>
            {gi > 0 && <div className="bg-grey2 mx-[14px] my-[4px] h-px" />}
            <div
              className={twMerge(
                "grid gap-[6px] px-[12px] py-[10px]",
                group.length > 1 ? "grid-cols-2" : "grid-cols-1",
              )}
            >
              {group.map((p) => (
                <PresetButton key={p.key} {...props} preset={p} />
              ))}
            </div>
          </Fragment>
        ))}
      </div>
    </>
  );
};
